import React, { useEffect, useRef, useState } from 'react'
import { Icon, Input, Select } from '@arnsbogroup/react-voicearchive'
import SupplierSelect from 'components/SupplierSelect'
import { FormattedMessage, useIntl } from 'react-intl'
import messages from './messages'

const TaskRow = ({
    task,
    taskTypeOptions = [],
    onTaskUpdate,
    onTaskDelete,
    versionIndex,
    taskIndex,
    versionLanguageId,
    saving,
    bulkAddTask = null,
    focus = false,
    mediaOptions=[],
    territoryMarketOptions=[],
    usageOptions=[]
  }) => {
  const { formatMessage } = useIntl()
  const [expandAdditionalInfo, setExpandAdditionalInfo] = useState(false)

  const disabled = task?.supplier?.uid || saving
  if (bulkAddTask && !task) {
    task = bulkAddTask
  }

  const parentRef = useRef(null);
  useEffect(() => {
    if (focus) {
      const supplierSelectElement = parentRef.current.querySelector('input');
      if (supplierSelectElement) {
        supplierSelectElement.focus();
      }
    }
  }, [focus]);
  

  const getTaskTypeValue = (taskType) => {
    let taskTypeId = taskType
    if (taskType !== null && typeof taskType === 'object') {
      taskTypeId = taskType?.uid ?? 2
    }

    const foundTaskType = taskTypeOptions ? taskTypeOptions.find(({uid}) => uid == taskTypeId) : null
    if (foundTaskType) {
      return {value: foundTaskType?.uid, label: foundTaskType?.name, shortname: foundTaskType?.shortname}
    }
    return null
  }

  const missingUsage = !task?.medias?.length || !task?.territoryMarkets?.length || !task?.usage?.uid

  return (
    <div className='bg-white p-3 rounded border border-gray-300 relative'>
      {getTaskTypeValue(task.type)?.label == 'Usage' && missingUsage &&
        <div class="absolute top-0.5 right-0.5 transform translate-x-2 -translate-y-2">
          <Icon title={formatMessage(messages.missingUsage)} className="text-negative h-5 w-5" icon={['fa', 'exclamation-circle']}/>
        </div>
      }
      <div key={`task-${task.uid}`} className="w-full flex items-center space-x-4">
        <div className="w-2/5" ref={parentRef}>
          <SupplierSelect
            value={task.preferredSupplier?.uid ? {value: task.preferredSupplier.uid, label: task.preferredSupplier.userid} : null}
            onChange={(supplier) => onTaskUpdate(versionIndex, taskIndex, 'preferredSupplier', supplier)}
            searchLanguageId={!['PR', 'TR'].includes(getTaskTypeValue(task.type)?.shortname) ? versionLanguageId : null}
            label={false}
            isDisabled={disabled}
            className="rounded bg-white"
          />
        </div>
        <div className="w-1/5">
          <Select
            required
            className="rounded bg-white"
            value={getTaskTypeValue(task.type)}
            onChange={(taskType) => onTaskUpdate(versionIndex, taskIndex, 'type', taskType?.value)}
            options={taskTypeOptions?.map(({ uid, name }) => ({ value: uid, label: name })) }
            isDisabled={disabled}
          />
        </div>

        <div className={`rounded border w-2/5 p-2 flex justify-between items-center ${task.disabled ? 'bg-gray-100 border-gray-200' : 'bg-white border-gray-300 '}`}>
          <Input
            size="sm"
            type="number"
            className="border-none"
            onChange={({ target: {value} }) => onTaskUpdate(versionIndex, taskIndex, 'cost', value)}
            value={task.cost}
            placeholder="Cost"
            disabled={disabled}
          />

          <CurrencySelect
            value={task.costcurrency ?? 'EUR'}
            onChange={(iso) => onTaskUpdate(versionIndex, taskIndex, 'costcurrency', iso)}
            disabled={disabled}
          />

          <Input
            size="sm"
            type="number"
            className="border-none"
            onChange={({ target: {value} }) => onTaskUpdate(versionIndex, taskIndex, 'price', value)}
            value={task.price}
            placeholder="Price"
            disabled={disabled}
          />

          <CurrencySelect
            value={task.pricecurrency}
            onChange={(iso) => onTaskUpdate(versionIndex, taskIndex, 'pricecurrency', iso)}
            disabled={disabled}
          />

          <Input
            size="sm"
            type="number"
            className="border-none"
            onChange={({ target: {value} }) => onTaskUpdate(versionIndex, taskIndex, 'discount', value)}
            value={task.discount}
            placeholder="Discount"
            disabled={disabled}
          />
          %
        </div>
        
        <div className='w-1/8 my-auto text-right'>
          <Icon
            onClick={() => disabled ? undefined : onTaskDelete(versionIndex, taskIndex)}
            className={`${disabled ? 'text-gray-300 cursor-not-allowed' : 'text-gray-700 cursor-pointer'} border-none`}
            icon={['fa', 'times']}
          />
        </div>
      </div>
      {getTaskTypeValue(task.type)?.label == 'Usage' &&
        <div className='pt-2'>
          {expandAdditionalInfo &&
              <div className='mb-2'>
                <div className="text-sm font-medium space-x-1 flex">
                  <label className="block text-gray-700">
                    <FormattedMessage {...messages.usageRights} />
                  </label>
                </div>
                <div className='flex items-center space-x-4'>
                  <div className='w-1/3'>
                    <Select
                      className="rounded bg-white"
                      isDisabled={disabled}
                      value={task?.medias ? task.medias.map(({ uid, name }) => ({
                          value: uid,
                          label: name,
                        })).filter((media) => media.value) : []}
                      onChange={(values) => onTaskUpdate(versionIndex, taskIndex, 'medias', !values ? [] : values.map(({ label, value }) => ({
                        name: label,
                        uid: value,
                      })))}
                      placeholder={formatMessage(messages.mediaPlaceholder)}
                      isClearable
                      isMulti
                      cacheOptions
                      defaultOptions
                      options={mediaOptions?.map(({ uid, name }) => ({ value: +uid, label: name }))}
                    />
                  </div>
                  <div className='w-1/3'>
                    <Select
                      className="rounded bg-white"
                      isDisabled={disabled}
                      value={task?.territoryMarkets ? task.territoryMarkets.map(({ uid, name }) => ({
                          value: uid,
                          label: name,
                        })).filter((territoryMarket) => territoryMarket.value) : []}
                      onChange={(values) => onTaskUpdate(versionIndex, taskIndex, 'territoryMarkets', !values ? [] : values.map(({ label, value }) => ({
                        name: label,
                        uid: value,
                      })))}
                      placeholder={formatMessage(messages.territoryMarketPlaceholder)}
                      isClearable
                      isMulti
                      cacheOptions
                      defaultOptions
                      options={territoryMarketOptions?.map(({ uid, name }) => ({ value: +uid, label: name }))}
                    />
                  </div>
                  <div className='w-1/3'>
                    <Select
                      className="rounded bg-white"
                      isDisabled={disabled}
                      value={task?.usage ? {label: task?.usage?.name, value: task?.usage?.uid} : null}
                      onChange={(value) => onTaskUpdate(versionIndex, taskIndex, 'usage', value?.value ? {
                        name: value?.label,
                        uid: value?.value
                      } : null)}
                      placeholder={formatMessage(messages.usagePlaceholder)}
                      isClearable
                      cacheOptions
                      defaultOptions
                      options={usageOptions?.map(({ uid, name }) => ({ value: +uid, label: name }))}
                    />
                  </div>
                </div>
              </div>
          }
          <Icon className="mt-1 w-full text-center cursor-pointer" onClick={() => setExpandAdditionalInfo(!expandAdditionalInfo)} icon={['fa', expandAdditionalInfo ? 'chevron-up' : 'chevron-down']}/>
        </div>
      }
    </div>
  )
}

const CurrencySelect = ({ value, onChange, ...props }) => (
  <select value={value} onChange={({ target }) => onChange(target.value)} className="h-6 w-1/3 bg-white focus:outline-none focus:ring focus:border-blue-300" {...props}>
    { ['EUR', 'DKK', 'USD', 'GBP', 'ZAR', 'NOK', 'SEK'].map((iso) => (
      <option key={iso} value={iso}>{ iso }</option>
    )) }
  </select>
)

export default TaskRow
